import { useState } from 'react';
import { Card, CardHeader, CardContent, IconButton, Box, Typography, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { ResponsivePie } from '@nivo/pie';
import { chartTheme } from './chartTheme';
import { getColorSet } from './colorUtils';
import { useTheme } from '@mui/material/styles';
import { DISTRIBUTION_CHART_COLORS } from './colorUtils';

interface DonutDataItem {
  id: string;
  label: string;
  value: number;
  formattedValue: string;
  color?: string;
  data?: {
    totalLeads?: number;
    convertedLeads?: number;
    channel?: string;  // for subchannel data
  };
}

interface DonutChartProps {
  title: string;
  data: DonutDataItem[];
  compact?: boolean;
}

interface ColorSet {
  main: string;
  gradient: [string, string];
}

type ColorKey = 
  // Product colors
  | 'HOSPITAL_CASH'
  | 'PERSONAL_ACCIDENT_FAMILY'
  | 'TOP_UP_PLANS'
  | 'CRITICAL_ILLNESS_RIDER'
  | 'INDIVIDUAL_HEALTH_PREMIUM'
  | 'SENIOR_CITIZEN_HEALTH'
  | 'PERSONAL_ACCIDENT_INDIVIDUAL'
  | 'FAMILY_FLOATER_PREMIUM'
  // Channel colors
  | 'DIRECT'
  | 'AGENCY'
  | 'BANCASSURANCE'
  | 'BROKERS'
  | 'CORPORATE_AGENTS'
  | 'ONLINE';

const CHOLA_COLORS: Record<ColorKey, ColorSet> = {
  // Product colors
  HOSPITAL_CASH: {
    main: '#003087',    // Chola Deep Blue
    gradient: ['#003087', '#004AAD']
  },
  PERSONAL_ACCIDENT_FAMILY: {
    main: '#0066CC',    // Medium Blue (replaced Red)
    gradient: ['#0066CC', '#0080FF']
  },
  TOP_UP_PLANS: {
    main: '#FFB81C',    // Chola Yellow
    gradient: ['#FFB81C', '#FFD060']
  },
  CRITICAL_ILLNESS_RIDER: {
    main: '#36A2EB',    // Bright Blue
    gradient: ['#36A2EB', '#2493DC']
  },
  INDIVIDUAL_HEALTH_PREMIUM: {
    main: '#00A499',    // Chola Teal
    gradient: ['#00A499', '#00C5B8']
  },
  SENIOR_CITIZEN_HEALTH: {
    main: '#7030A0',    // Chola Purple
    gradient: ['#7030A0', '#8B44BD']
  },
  PERSONAL_ACCIDENT_INDIVIDUAL: {
    main: '#4BC0C0',    // Teal (replaced Coral Red)
    gradient: ['#4BC0C0', '#3DA8A8']
  },
  FAMILY_FLOATER_PREMIUM: {
    main: '#4A90E2',    // Sky Blue
    gradient: ['#4A90E2', '#73B4FF']
  },
  // Channel colors
  DIRECT: {
    main: '#36A2EB',    // Bright Blue
    gradient: ['#36A2EB', '#2493DC']
  },
  AGENCY: {
    main: '#4BC0C0',    // Teal
    gradient: ['#4BC0C0', '#3DA8A8']
  },
  BANCASSURANCE: {
    main: '#9966FF',    // Purple
    gradient: ['#9966FF', '#8A5BF2']
  },
  BROKERS: {
    main: '#5C6BC0',    // Indigo Blue (replaced Pink)
    gradient: ['#5C6BC0', '#4A59A9']
  },
  CORPORATE_AGENTS: {
    main: '#FFCD56',    // Yellow
    gradient: ['#FFCD56', '#EBBE47']
  },
  ONLINE: {
    main: '#4C72DA',    // Royal Blue
    gradient: ['#4C72DA', '#3D63CB']
  }
};

// Helper function to get color for an item
const getItemColor = (item: string): { main: string; gradient: string[] } => {
  return getColorSet(item);
};

interface TooltipProps {
  label: string;
  value: number;
  color: string;
}

interface ChartData {
  id: string;
  label: string;
  value: number;
  color: string;
}

export function DonutChart({ title, data, compact = false }: DonutChartProps) {
  const theme = useTheme();

  const handleExport = () => {
    const csvContent = [
      ['Label', 'Value', 'Conversion %'].join(','),
      ...data.map(item => [
        item.label,
        item.data?.totalLeads || '',
        item.formattedValue || item.value
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title?.toLowerCase().replace(/\s+/g, '_') || 'chart'}.csv`;
    link.click();
  };

  return (
    
        <ResponsivePie
          data={data}
          margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
          innerRadius={0.6}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          colors={DISTRIBUTION_CHART_COLORS}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#000000"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor="#000000"
          arcLabel={d => `${d.value}%`}
          arcLabelsRadiusOffset={0.5}
          defs={[
            {
              id: 'dots',
              type: 'patternDots',
              background: 'inherit',
              color: 'rgba(255, 255, 255, 0.3)',
              size: 4,
              padding: 1,
              stagger: true
            },
            {
              id: 'lines',
              type: 'patternLines',
              background: 'inherit',
              color: 'rgba(255, 255, 255, 0.3)',
              rotation: -45,
              lineWidth: 6,
              spacing: 10
            }
          ]}
          tooltip={({ datum }) => (
            <div style={{
              background: 'white',
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '14px',
              maxWidth: '220px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                gap: '8px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: datum.color,
                  borderRadius: '2px'
                }} />
                <strong style={{ color: datum.color }}>
                  {datum.label}
                </strong>
              </div>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                {datum.data.data?.totalLeads && (
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px'
                  }}>
                    <span style={{ color: '#666' }}>Total Base:</span>
                    <strong>{datum.data.data.totalLeads.toLocaleString()}</strong>
                  </div>
                )}
                {datum.data.data?.convertedLeads && (
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px'
                  }}>
                    <span style={{ color: '#666' }}>Converted:</span>
                    <strong>{datum.data.data.convertedLeads.toLocaleString()}</strong>
                  </div>
                )}
                {datum.data.data?.channel && (
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px'
                  }}>
                    <span style={{ color: '#666' }}>Channel:</span>
                    <strong>{datum.data.data.channel}</strong>
                  </div>
                )}
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px',
                  borderTop: '1px solid #eee',
                  marginTop: '4px',
                  paddingTop: '4px'
                }}>
                  <span style={{ color: '#666' }}>Conversion %:</span>
                  <strong>{datum.formattedValue || `${datum.value}%`}%</strong>
                </div>
              </div>
            </div>
          )}
        />
  );
} 